import classNames from 'classnames';
import { isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Carousel from 'components/Carousel';
import Chevron, { DIRECTIONS, SIZES } from 'components/Chevron';
import IconButton from 'components/IconButton';
import { MediumUp } from 'components/MediaQuery';
import Paragraph from 'components/Paragraph';
import { mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

import PdpRecommendationsCard from './PdpRecommendationsCard';

const ADDITIONAL_RECOMMENDATIONS_EXPERIMENT_TYPES = {
  CAROUSEL: 'CAROUSEL',
  GRID: 'GRID',
};
const CAROUSEL_SLIDES_PER_VIEWPORT = 2.06;

const PdpSidebarRecs = ({
  additionalRecommendationsExperimentData,
  heading,
  products,
  showColorway,
  trackEvent,
  dyCampaignData,
}) => {
  let recommendedProducts = [];
  const { type: additionalRecommendationsType } =
    additionalRecommendationsExperimentData.dynamicYieldIdentifiers || {};
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  if (!heading) return null;
  if (!products || !products.length) return null;

  if (
    products?.length > 2 &&
    isEmpty(additionalRecommendationsExperimentData?.dynamicYieldIdentifiers)
  ) {
    recommendedProducts = products.slice(0, 2);
  } else {
    recommendedProducts = products;
  }

  const handleSlideSelect = index => {
    setActiveSlideIndex(index);
  };

  return (
    <div className="PdpSidebarRecs">
      <div className="PdpSidebarRecs-title">
        <Paragraph>
          <b>{heading}</b>
        </Paragraph>
      </div>
      {(isEmpty(additionalRecommendationsExperimentData?.dynamicYieldIdentifiers) ||
        additionalRecommendationsType === ADDITIONAL_RECOMMENDATIONS_EXPERIMENT_TYPES.GRID) && (
        <div className="PdpSidebarRecs-cards">
          {map(recommendedProducts, (product, i) => {
            const analyticsProperties = {
              category: product.productData.parent_category,
              gender: product.productData.gender,
              name: product.productData.name,
              handle: product.productData.handle,
              recommendationHeading: heading,
              carouselCardPosition: i,
              ...dyCampaignData,
            };

            return (
              <PdpRecommendationsCard
                key={i}
                imageLink={product.productData.image_url}
                productName={product.productData.name}
                linkUrl={product.productData.url}
                colorway={product.productData.display_color_no_sole}
                showColorway={showColorway}
                slotId={product.slotId}
                onClick={trackEvent}
                analyticsProperties={analyticsProperties}
              />
            );
          })}
        </div>
      )}

      {additionalRecommendationsType === ADDITIONAL_RECOMMENDATIONS_EXPERIMENT_TYPES.CAROUSEL && (
        <div className="PdpSidebarRecs-carousel-wrapper">
          <Carousel
            currentSlide={activeSlideIndex}
            options={{
              loop: false,
              draggable: recommendedProducts.length > 2,
              perPage: CAROUSEL_SLIDES_PER_VIEWPORT,
            }}
            onChange={currentSlide => handleSlideSelect(currentSlide)}
          >
            {map(recommendedProducts, (product, i) => {
              const analyticsProperties = {
                category: product.productData.parent_category,
                gender: product.productData.gender,
                name: product.productData.name,
                handle: product.productData.handle,
                recommendationHeading: heading,
                carouselCardPosition: i,
                ...dyCampaignData,
              };

              return (
                <div className="PdpSidebarRecs-slide-wrapper">
                  <PdpRecommendationsCard
                    key={i}
                    imageLink={product.productData.image_url}
                    productName={product.productData.name}
                    linkUrl={product.productData.url}
                    colorway={product.productData.display_color_no_sole}
                    showColorway={showColorway}
                    slotId={product.slotId}
                    onClick={trackEvent}
                    analyticsProperties={analyticsProperties}
                  />
                </div>
              );
            })}
          </Carousel>
          <MediumUp>
            <div
              className={classNames('PdpSidebarRecs-arrow', 'PdpSidebarRecs-left-arrow', {
                'PdpSidebarRecs-arrow--active': activeSlideIndex > 0,
              })}
            >
              <IconButton onClick={() => handleSlideSelect(activeSlideIndex - 1)}>
                <Chevron
                  fillColor="colors-black-80"
                  direction={DIRECTIONS.LEFT}
                  size={SIZES.MEDIUM}
                />
              </IconButton>
            </div>

            <div
              className={classNames('PdpSidebarRecs-arrow', 'PdpSidebarRecs-right-arrow', {
                'PdpSidebarRecs-arrow--active':
                  activeSlideIndex + CAROUSEL_SLIDES_PER_VIEWPORT < recommendedProducts.length,
              })}
            >
              <IconButton onClick={() => handleSlideSelect(activeSlideIndex + 1)}>
                <Chevron
                  fillColor="colors-black-80"
                  direction={DIRECTIONS.RIGHT}
                  size={SIZES.MEDIUM}
                />
              </IconButton>
            </div>
          </MediumUp>
        </div>
      )}
      <style jsx>{`
        .PdpSidebarRecs {
          display: flex;
          flex-direction: column;
          margin-top: ${Theme.get('spacing') * 3}px;
        }

        .PdpSidebarRecs .PdpSidebarRecs-cards {
          display: grid;
          grid-template-columns: repeat(2, minmax(100px, 1fr));
          gap: ${Theme.get('spacing') * 3}px;
        }

        .PdpSidebarRecs-carousel-wrapper {
          position: relative;
        }

        .PdpSidebarRecs-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          opacity: 0;
          visibility: hidden;
        }

        .PdpSidebarRecs-arrow--active {
          opacity: 1;
          visibility: visible;
        }

        .PdpSidebarRecs-left-arrow {
          left: 0;
        }

        .PdpSidebarRecs-right-arrow {
          right: 0;
        }
      `}</style>
      <style jsx global>{`
        .PdpSidebarRecs .PdpSidebarRecs-title p.Paragraph {
          font-size: 22px;
          letter-spacing: 0.5px;
          line-height: 1.31;
        }

        .PdpSidebarRecs .PdpSidebarRecs-slide-wrapper {
          margin: 0 ${Theme.get('spacing-narrow')}px;
          display: flex;
          height: 100%;
        }

        .PdpSidebarRecs .Carousel > div {
          display: flex;
          padding: ${Theme.get('spacing') * 2.5}px 0;
        }

        .PdpSidebarRecs .Carousel {
          position: relative;
          overflow: visible !important;
          left: -${Theme.get('spacing')}px;
        }

        @media only screen and (max-width: 991px) {
          .Pdp__product-recommendations .PdpSidebarRecs-title p.Paragraph {
            font-size: ${Theme.get('spacing') * 3}px;
            line-height: 1.36;
          }
        }

        .PdpSidebarRecs .PdpSidebarRecs-cards .PdpRecommendationsCard:only-child {
          margin: 0;
        }

        @media ${mediumUp} {
          .PdpSidebarRecs .PdpSidebarRecs-cards .PdpRecommendationsCard:only-child {
            flex: 0 0 50%;
          }

          .PdpSidebarRecs .Carousel {
            overflow: hidden !important;
          }
        }

        .PdpSidebarRecs-arrow button.IconButton {
          display: flex;
          justify-content: center;
          align-items: center;
          border: 1px solid ${Theme.get('colors-black-20')};
          width: ${Theme.get('spacing') * 8}px;
          height: ${Theme.get('spacing') * 8}px;
          border-radius: 50%;
          background-color: ${Theme.get('colors-white')};
          box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.25);
          transition: border-color ease-in-out ${Theme.get('transition-duration') / 2}ms;
        }

        .PdpSidebarRecs-arrow button.IconButton:hover,
        button.IconButton.focused {
          border-color: ${Theme.get('colors-black-60')};
        }
      `}</style>
    </div>
  );
};

PdpSidebarRecs.propTypes = {
  additionalRecommendationsExperimentData: PropTypes.shape({
    dynamicYieldIdentifiers: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
  heading: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productData: PropTypes.shape({
        image_link: PropTypes.string,
        name: PropTypes.string,
        url: PropTypes.string,
      }),
    })
  ),
  dyCampaignData: PropTypes.shape({}).isRequired,
  showColorway: PropTypes.bool,
  trackEvent: PropTypes.func,
};

PdpSidebarRecs.defaultProps = {
  additionalRecommendationsExperimentData: {},
  heading: '',
  products: [],
  showColorway: false,
  trackEvent: () => {},
};

export default PdpSidebarRecs;
